import type { ComponentType } from 'react'
import type { FieldExtraPropsContext, FieldRendererProps, FieldTypeConfig } from './types'

import ButtonArrayField, { buttonArrayFieldConfig } from './ButtonArrayField'
import CardArrayField, { cardArrayFieldConfig } from './CardArrayField'
import CheckboxField, { checkboxFieldConfig } from './CheckboxField'
import CustomField from './CustomField'
import DateField from './DateField'
import DateRangeArrayField from './DateRangeArrayField'
import DatetimeField, { datetimeFieldConfig } from './DatetimeField'
import DaysAndTimesField, { daysAndTimesFieldConfig } from './DaysAndTimesField'
import FaqArrayField, { faqArrayFieldConfig } from './FaqArrayField'
import ImageField from './ImageField'
import LinkArrayField, { linkArrayFieldConfig } from './LinkArrayField'
import MenuArrayField, { menuArrayFieldConfig } from './MenuArrayField'
import PasswordField from './PasswordField'
import RichtextField from './RichtextField'
import SelectField, { selectFieldConfig } from './SelectField'
import StepTextArrayField, { stepTextArrayFieldConfig } from './StepTextArrayField'
import TextField from './TextField'
import TextareaField from './TextareaField'
import TimeField from './TimeField'
import TurnstileField, { turnstileFieldConfig } from './TurnstileField'
import UserSearchField from './UserSearchField'

export type FieldComponent = ComponentType<FieldRendererProps>

/** Default behaviour when a field type does not provide a config. */
const DEFAULT_FIELD_CONFIG: Required<
    Pick<FieldTypeConfig, 'getInitialValue' | 'getNewArrayItem' | 'cloneForDuplicate'>
> = {
    getInitialValue: f =>
        f.defaultValue !== undefined && f.defaultValue !== null ? f.defaultValue : '',
    getNewArrayItem: () => ({ text: '' }),
    cloneForDuplicate: (_, item) =>
        typeof item === 'object' && item !== null ? { ...(item as object) } : item
}

type FieldDef = {
    type: string
    component: FieldComponent
    config?: Partial<FieldTypeConfig>
}

const FIELD_DEFINITIONS: FieldDef[] = [
    { type: 'text', component: TextField },
    { type: 'email', component: TextField },
    { type: 'number', component: TextField },
    { type: 'password', component: PasswordField },
    { type: 'textarea', component: TextareaField },
    { type: 'richtext', component: RichtextField },
    { type: 'checkbox', component: CheckboxField, config: checkboxFieldConfig },
    { type: 'select', component: SelectField, config: selectFieldConfig },
    { type: 'image', component: ImageField },
    { type: 'date', component: DateField },
    { type: 'time', component: TimeField },
    { type: 'datetime', component: DatetimeField, config: datetimeFieldConfig },
    { type: 'daysAndTimes', component: DaysAndTimesField, config: daysAndTimesFieldConfig },
    { type: 'dateRangeArray', component: DateRangeArrayField },
    { type: 'turnstile', component: TurnstileField, config: turnstileFieldConfig },
    { type: 'custom', component: CustomField },
    { type: 'userSearch', component: UserSearchField },
    { type: 'buttonArray', component: ButtonArrayField, config: buttonArrayFieldConfig },
    { type: 'linkArray', component: LinkArrayField, config: linkArrayFieldConfig },
    { type: 'menuArray', component: MenuArrayField, config: menuArrayFieldConfig },
    { type: 'faqArray', component: FaqArrayField, config: faqArrayFieldConfig },
    { type: 'stepTextArray', component: StepTextArrayField, config: stepTextArrayFieldConfig },
    { type: 'cardArray', component: CardArrayField, config: cardArrayFieldConfig }
]

/** type -> component. Used by GenericForm.renderField. */
export const FIELD_COMPONENTS: Record<string, FieldComponent> = Object.fromEntries(
    FIELD_DEFINITIONS.map(d => [d.type, d.component])
)

type MergedConfig = typeof DEFAULT_FIELD_CONFIG & Partial<FieldTypeConfig>

function mergeConfig(def: FieldDef): MergedConfig {
    const effective: MergedConfig = {
        ...DEFAULT_FIELD_CONFIG,
        ...(def.config || {})
    }
    if (
        effective.extraPropsKind === 'array' &&
        !(def.config && 'getInitialValue' in def.config)
    ) {
        effective.getInitialValue = f => f.defaultValue ?? []
    }
    return effective
}

const FIELD_CONFIG: Record<string, MergedConfig> = Object.fromEntries(
    FIELD_DEFINITIONS.map(d => [d.type, mergeConfig(d)])
)

export function getInitialValue(field: TFormField): unknown {
    const cfg = FIELD_CONFIG[field.type] ?? DEFAULT_FIELD_CONFIG
    return cfg.getInitialValue(field)
}

export function getNewArrayItem(field: TFormField): unknown {
    const cfg = FIELD_CONFIG[field.type] ?? DEFAULT_FIELD_CONFIG
    return cfg.getNewArrayItem(field)
}

export function cloneArrayItemForDuplicate(field: TFormField, item: unknown): unknown {
    const cfg = FIELD_CONFIG[field.type] ?? DEFAULT_FIELD_CONFIG
    return cfg.cloneForDuplicate(field, item)
}

export function getExtraPropsForField(
    field: TFormField,
    ctx: FieldExtraPropsContext
): Partial<FieldRendererProps> {
    const kind = FIELD_CONFIG[field.type]?.extraPropsKind
    if (kind === 'array') {
        return {
            formState: ctx.formState,
            onArrayChange: ctx.onArrayChange,
            onAddArrayItem: ctx.onAddArrayItem,
            onRemoveArrayItem: ctx.onRemoveArrayItem,
            onDuplicateArrayItem: ctx.onDuplicateArrayItem
        }
    }
    if (kind === 'checkbox') {
        return {
            updatingCheckboxes: ctx.updatingCheckboxes,
            onCheckboxChange: ctx.onCheckboxChange
        }
    }
    if (kind === 'turnstile') {
        return { turnstileRef: ctx.turnstileRef }
    }
    return {}
}

export function getFieldValidationErrors(
    fields: TFormField[],
    formState: { [key: string]: unknown }
): { [key: string]: string } {
    const errors: { [key: string]: string } = {}
    for (const field of fields) {
        const validate = FIELD_CONFIG[field.type]?.validate
        if (validate) {
            const result = validate(field, formState)
            if (result && typeof result === 'object') Object.assign(errors, result)
        }
    }
    return errors
}

export type { FieldExtraPropsContext } from './types'

export { default as FieldWrapper } from './FieldWrapper'
export type { FieldRendererProps } from './types'
